import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import {
  Shield,
  AlertTriangle,
  Clock,
  Target,
  Activity,
  CheckCircle,
  XCircle,
  Calendar,
  Globe,
  Bug,
  AlertCircle,
  Info,
  Download,
  FileText,
  TrendingUp,
  Award,
  Lock,
  ChevronDown,
  ChevronUp,
  Terminal,
  Code,
  BarChart2,
  PieChart,
  Database
} from "lucide-react";
import { apiRequest } from "../utils/api";
import { Bar, Pie } from 'react-chartjs-2';
import Chart from 'chart.js/auto';

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  
  * {
    box-sizing: border-box;
  }
  
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    margin: 0;
    padding: 0;
    background: #0a0a0a;
    color: #ffffff;
    overflow-x: hidden;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 300px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%);
    z-index: 0;
  }
`;

const Header = styled.div`
  position: relative;
  z-index: 1;
  background: rgba(15, 15, 15, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(59, 130, 246, 0.2);
  padding: 3rem 0;
  margin-bottom: 3rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 2px;
    background: linear-gradient(90deg, transparent, #3b82f6, transparent);
  }
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  font-weight: 900;
  margin: 0 0 1rem 0;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #94a3b8;
  margin: 0 0 2rem 0;
  font-weight: 400;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const PremiumBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #000;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 20px rgba(251, 191, 36, 0.3);
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 6rem 2rem;
  position: relative;
  z-index: 1;
`;

const Section = styled.section`
  margin-bottom: 4rem;
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;
`;

const SectionTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  margin: 0 0 2rem 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #ffffff;
  
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, rgba(59, 130, 246, 0.5), transparent);
    margin-left: 2rem;
  }
`;

const ExecutiveSummary = styled.div`
  background: linear-gradient(135deg, rgba(15, 15, 15, 0.9) 0%, rgba(30, 30, 30, 0.9) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 24px;
  padding: 3rem;
  margin-bottom: 3rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3b82f6, #8b5cf6, #06b6d4);
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const SummaryCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    border-color: rgba(59, 130, 246, 0.5);
    box-shadow: 0 10px 40px rgba(59, 130, 246, 0.2);
  }
`;

const SummaryValue = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: ${props => props.color || '#3b82f6'};
  margin-bottom: 0.5rem;
`;

const SummaryLabel = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(25, 25, 25, 0.95) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    border-color: rgba(59, 130, 246, 0.5);
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 60px rgba(59, 130, 246, 0.2);
    
    &::before {
      left: 100%;
    }
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const StatIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${props => props.color || '#3b82f6'}20, ${props => props.color || '#3b82f6'}40);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color || '#3b82f6'};
  box-shadow: 0 8px 32px ${props => props.color || '#3b82f6'}30;
`;

const StatValue = styled.div`
  font-size: 2.75rem;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 0.5rem;
  text-align: right;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-align: right;
`;

const VulnerabilityGrid = styled.div`
  display: grid;
  gap: 2rem;
`;

const VulnCard = styled.div`
  background: linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(25, 25, 25, 0.95) 100%);
  backdrop-filter: blur(20px);
  border: 2px solid ${props => getSeverityColor(props.severity)}40;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => getSeverityColor(props.severity)}, ${props => getSeverityColor(props.severity)}80);
  }
  
  &:hover {
    border-color: ${props => getSeverityColor(props.severity)};
    transform: translateY(-8px);
    box-shadow: 0 25px 80px ${props => getSeverityColor(props.severity)}30;
  }
`;

const VulnHeader = styled.div`
  background: linear-gradient(135deg, ${props => getSeverityColor(props.severity)}15, ${props => getSeverityColor(props.severity)}25);
  padding: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${props => getSeverityColor(props.severity)}30;
`;

const VulnTitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const VulnIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => getSeverityColor(props.severity)}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => getSeverityColor(props.severity)};
`;

const VulnTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: #ffffff;
`;

const VulnActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SeverityBadge = styled.span`
  background: linear-gradient(135deg, ${props => getSeverityColor(props.severity)}, ${props => getSeverityColor(props.severity)}80);
  color: #ffffff;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 16px ${props => getSeverityColor(props.severity)}40;
`;

const VulnContent = styled.div`
  max-height: ${props => props.expanded ? 'none' : '0'};
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${props => props.expanded ? '1' : '0'};
`;

const VulnDetails = styled.div`
  padding: 2rem;
`;

const DetailCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(59, 130, 246, 0.3);
    background: rgba(255, 255, 255, 0.08);
  }
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #3b82f6;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailValue = styled.div`
  color: #e2e8f0;
  line-height: 1.6;
  font-size: 0.95rem;
`;

// Make sure your DetailGrid is defined as:
const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const DetailSection = styled.div`
  background: linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.8) 100%);
  border-radius: 16px;
  padding: 1.5rem;
  border-left: 4px solid ${props => props.color || '#3b82f6'};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const DetailSectionTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: ${props => props.color || '#3b82f6'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailSectionContent = styled.div`
  color: #e2e8f0;
  line-height: 1.7;
  font-size: 0.95rem;
`;

const CodeBlock = styled.pre`
  background: linear-gradient(135deg, #111111 0%, #1a1a1a 100%);
  border: 1px solid #333333;
  border-radius: 12px;
  padding: 1.5rem;
  overflow-x: auto;
  font-family: 'JetBrains Mono', 'Monaco', 'Menlo', monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #e2e8f0;
  position: relative;
  margin-top: 1rem;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #22c55e, #06b6d4);
  }
`;

const ImagePreview = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  margin-top: 1rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(59, 130, 246, 0.5);
    transform: scale(1.02);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 2rem;
`;

const Spinner = styled.div`
  width: 64px;
  height: 64px;
  border: 4px solid rgba(59, 130, 246, 0.2);
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 1.25rem;
  color: #94a3b8;
  font-weight: 500;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const ErrorContainer = styled.div`
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.2));
  border: 2px solid #ef4444;
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: #ef4444;
  backdrop-filter: blur(20px);
`;

const NoVulnsContainer = styled.div`
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(34, 197, 94, 0.2));
  border: 2px solid #22c55e;
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: #22c55e;
  backdrop-filter: blur(20px);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border: none;
  color: white;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #1d4ed8, #1e40af);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
  }
`;

const ChartContainer = styled.div`
  background: linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(25, 25, 25, 0.95) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  height: 300px;
  
  &:hover {
    border-color: rgba(59, 130, 246, 0.5);
    transform: translateY(-4px);
    box-shadow: 0 20px 60px rgba(59, 130, 246, 0.2);
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const getSeverityColor = (severity) => {
  switch (severity?.toLowerCase()) {
    case 'critical':
      return '#dc2626';
    case 'high':
      return '#ea580c';
    case 'medium':
      return '#d97706';
    case 'low':
      return '#16a34a';
    case 'info':
      return '#2563eb';
    default:
      return '#6b7280';
  }
};

const getSeverityIcon = (severity) => {
  switch (severity?.toLowerCase()) {
    case 'critical':
      return <XCircle size={24} />;
    case 'high':
      return <AlertTriangle size={24} />;
    case 'medium':
      return <AlertCircle size={24} />;
    case 'low':
      return <Info size={24} />;
    default:
      return <Bug size={24} />;
  }
};

const AdvancedScanReport = ({ scanData = [], vulnerabilityData = [] }) => {
  const [expandedVulns, setExpandedVulns] = useState(new Set());

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const toggleVuln = (vulnId) => {
    const newExpanded = new Set(expandedVulns);
    if (newExpanded.has(vulnId)) {
      newExpanded.delete(vulnId);
    } else {
      newExpanded.add(vulnId);
    }
    setExpandedVulns(newExpanded);
  };

  const getTotalVulns = () => {
    return vulnerabilityData.reduce((total, scan) => {
      return total + (scan.results ? Object.keys(scan.results).length : 0);
    }, 0);
  };

  const getSeverityStats = () => {
    const stats = { Critical: 0, High: 0, Medium: 0, Low: 0, Info: 0 };
    scanData.forEach(scan => {
      if (scan.scanResult) {
        Object.entries(scan.scanResult).forEach(([severity, count]) => {
          if (severity in stats) {
            stats[severity] += count;
          }
        });
      }
    });
    return stats;
  };

  const getSecurityScore = () => {
    const stats = getSeverityStats();
    const totalVulns = Object.values(stats).reduce((sum, count) => sum + count, 0);

    if (totalVulns === 0) return 100;

    const weightedScore = (
      stats.Critical * 25 +
      stats.High * 15 +
      stats.Medium * 8 +
      stats.Low * 3 +
      stats.Info * 1
    );

    return Math.max(0, 100 - weightedScore);
  };

  const severityStats = getSeverityStats();
  const totalVulns = getTotalVulns();
  const securityScore = getSecurityScore();
  const mainScan = scanData[0] || {};

  // Prepare data for charts
  const severityChartData = {
    labels: Object.keys(severityStats),
    datasets: [{
      label: 'Vulnerabilities by Severity',
      data: Object.values(severityStats),
      backgroundColor: [
        getSeverityColor('Critical'),
        getSeverityColor('High'),
        getSeverityColor('Medium'),
        getSeverityColor('Low'),
        getSeverityColor('Info')
      ],
      borderColor: [
        getSeverityColor('Critical'),
        getSeverityColor('High'),
        getSeverityColor('Medium'),
        getSeverityColor('Low'),
        getSeverityColor('Info')
      ],
      borderWidth: 1
    }]
  };

  const securityScoreData = {
    labels: ['Security Score', 'Risk Score'],
    datasets: [{
      label: 'Security Score',
      data: [securityScore, 100 - securityScore],
      backgroundColor: [
        securityScore >= 80 ? '#22c55e' : securityScore >= 60 ? '#f59e0b' : '#ef4444',
        '#1a1a1a'
      ],
      borderColor: [
        securityScore >= 80 ? '#22c55e' : securityScore >= 60 ? '#f59e0b' : '#ef4444',
        '#333333'
      ],
      borderWidth: 1
    }]
  };

  return (
    <>
      <GlobalStyle />
      <Container>
        <Content>
          <br></br>
          {/* Executive Summary */}
          <Section delay="0.1s">
            <ExecutiveSummary>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '2rem', fontWeight: '700', color: '#ffffff' }}>
                    Executive Summary
                  </h2>
                  <p style={{ margin: '0.5rem 0 0 0', color: '#94a3b8', fontSize: '1.1rem' }}>
                    Target: {mainScan.asset?.url || mainScan.asset?.ip || mainScan.url || "N/A"}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '3rem', fontWeight: '800', color: securityScore >= 80 ? '#22c55e' : securityScore >= 60 ? '#f59e0b' : '#ef4444' }}>
                    {securityScore}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#94a3b8', fontWeight: '600' }}>
                    SECURITY SCORE
                  </div>
                </div>
              </div>

              <SummaryGrid>
                <SummaryCard>
                  <SummaryValue color="#ef4444">{severityStats.Critical}</SummaryValue>
                  <SummaryLabel>Critical</SummaryLabel>
                </SummaryCard>
                <SummaryCard>
                  <SummaryValue color="#ea580c">{severityStats.High}</SummaryValue>
                  <SummaryLabel>High</SummaryLabel>
                </SummaryCard>
                <SummaryCard>
                  <SummaryValue color="#d97706">{severityStats.Medium}</SummaryValue>
                  <SummaryLabel>Medium</SummaryLabel>
                </SummaryCard>
                <SummaryCard>
                  <SummaryValue color="#16a34a">{severityStats.Low}</SummaryValue>
                  <SummaryLabel>Low</SummaryLabel>
                </SummaryCard>
                <SummaryCard>
                  <SummaryValue color="#2563eb">{totalVulns}</SummaryValue>
                  <SummaryLabel>Total Issues</SummaryLabel>
                </SummaryCard>
              </SummaryGrid>
            </ExecutiveSummary>
          </Section>

          {/* Scan Overview */}
          <Section delay="0.2s">
            <SectionTitle>
              <Shield size={28} />
              Scan Overview
            </SectionTitle>

            <StatsGrid>
              {scanData.map((scan, index) => (
                <StatCard key={scan._id || index}>
                  <StatHeader>
                    <StatIcon color="#3b82f6">
                      <Target size={28} />
                    </StatIcon>
                    <div>
                      <StatValue>{scan.progress}%</StatValue>
                      <StatLabel>Completion</StatLabel>
                    </div>
                  </StatHeader>

                  <DetailGrid>
                    <DetailCard>
                      <DetailLabel>
                        <Globe size={16} />
                        Target Asset
                      </DetailLabel>
                      <DetailValue>{scan.asset?.url || scan.asset?.ip || scan.url || "N/A"}</DetailValue>
                    </DetailCard>

                    <DetailCard>
                      <DetailLabel>
                        <Activity size={16} />
                        Status
                      </DetailLabel>
                      <DetailValue style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {scan.status === 'completed' ? (
                          <CheckCircle size={16} color="#22c55e" />
                        ) : (
                          <Clock size={16} color="#f59e0b" />
                        )}
                        {scan.status}
                      </DetailValue>
                    </DetailCard>

                    <DetailCard>
                      <DetailLabel>
                        <Calendar size={16} />
                        Start Time
                      </DetailLabel>
                      <DetailValue>{formatDate(scan.startTime)}</DetailValue>
                    </DetailCard>

                    <DetailCard>
                      <DetailLabel>
                        <Clock size={16} />
                        End Time
                      </DetailLabel>
                      <DetailValue>{formatDate(scan.endTime)}</DetailValue>
                    </DetailCard>

                    <DetailCard>
                      <DetailLabel>
                        <BarChart2 size={16} />
                        Scan Type
                      </DetailLabel>
                      <DetailValue>{scan.scanType || "Advanced Scan"}</DetailValue>
                    </DetailCard>

                    <DetailCard>
                      <DetailLabel>
                        <Database size={16} />
                        Scan Engine
                      </DetailLabel>
                      <DetailValue>{scan.scanEngine || "Premium Engine v3.2"}</DetailValue>
                    </DetailCard>
                  </DetailGrid>
                </StatCard>
              ))}
            </StatsGrid>

            {/* Vulnerability Charts */}
            <ChartsGrid>
              <ChartContainer>
                <Bar
                  data={severityChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: {
                          color: '#e2e8f0'
                        }
                      },
                      title: {
                        display: true,
                        text: 'Vulnerabilities by Severity',
                        color: '#ffffff',
                        font: {
                          size: 16,
                          weight: '600'
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          color: '#94a3b8'
                        },
                        grid: {
                          color: 'rgba(255, 255, 255, 0.05)'
                        }
                      },
                      x: {
                        ticks: {
                          color: '#94a3b8'
                        },
                        grid: {
                          color: 'rgba(255, 255, 255, 0.05)'
                        }
                      }
                    }
                  }}
                />
              </ChartContainer>

              <ChartContainer>
                <Pie
                  data={securityScoreData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: {
                          color: '#e2e8f0'
                        }
                      },
                      title: {
                        display: true,
                        text: 'Security Score Breakdown',
                        color: '#ffffff',
                        font: {
                          size: 16,
                          weight: '600'
                        }
                      }
                    }
                  }}
                />
              </ChartContainer>
            </ChartsGrid>
          </Section>

          {/* Vulnerabilities Section */}
          <Section delay="0.3s">
            <SectionTitle>
              <AlertTriangle size={28} />
              Detailed Vulnerability Analysis
            </SectionTitle>

            {vulnerabilityData.length === 0 ? (
              <NoVulnsContainer>
                <CheckCircle size={64} style={{ margin: '0 auto 2rem auto' }} />
                <div style={{ fontSize: '1.5rem', marginBottom: '1rem', fontWeight: '700' }}>
                  Excellent Security Posture
                </div>
                <div style={{ opacity: 0.9, fontSize: '1.1rem' }}>
                  No vulnerabilities detected in this advanced security assessment
                </div>
                <ActionButtons>
                  <ActionButton>
                    <Download size={16} />
                    Download Clean Report
                  </ActionButton>
                  <ActionButton>
                    <FileText size={16} />
                    Generate Certificate
                  </ActionButton>
                </ActionButtons>
              </NoVulnsContainer>
            ) : (
              <VulnerabilityGrid>
                {vulnerabilityData.map((scan) => {
                  const vulns = scan.results ? Object.entries(scan.results) : [];
                  return vulns.map(([vulnId, vuln]) => (
                    <VulnCard
                      key={vulnId}
                      severity={vuln.severity}
                      onClick={() => toggleVuln(vulnId)}
                    >
                      <VulnHeader severity={vuln.severity}>
                        <VulnTitleSection>
                          <VulnIcon severity={vuln.severity}>
                            {getSeverityIcon(vuln.severity)}
                          </VulnIcon>
                          <div>
                            <VulnTitle>{vuln.name}</VulnTitle>
                            <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                              Impact: {vuln.impact}
                            </div>
                          </div>
                        </VulnTitleSection>

                        <VulnActions>
                          <SeverityBadge severity={vuln.severity}>
                            {vuln.severity}
                          </SeverityBadge>
                          {expandedVulns.has(vulnId) ? (
                            <ChevronUp size={20} color={getSeverityColor(vuln.severity)} />
                          ) : (
                            <ChevronDown size={20} color={getSeverityColor(vuln.severity)} />
                          )}
                        </VulnActions>
                      </VulnHeader>

                      <VulnContent expanded={expandedVulns.has(vulnId)}>
                        <VulnDetails>
                          {/* Stack all items vertically with consistent spacing */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {/* OWASP Category (only shown when expanded) */}
                            {expandedVulns.has(vulnId) && (
                              <DetailCard>
                                <DetailLabel>
                                  <Shield size={16} />
                                  OWASP Category
                                </DetailLabel>
                                <DetailValue>{vuln.owaspCategory}</DetailValue>
                              </DetailCard>
                            )}

                            {/* Description */}
                            <DetailCard>
                              <DetailLabel>
                                <FileText size={16} />
                                Description
                              </DetailLabel>
                              <DetailValue>{vuln.description}</DetailValue>
                            </DetailCard>

                            {/* Business Impact */}
                            <DetailCard>
                              <DetailLabel>
                                <TrendingUp size={16} />
                                Business Impact
                              </DetailLabel>
                              <DetailValue>{vuln.impact}</DetailValue>
                            </DetailCard>

                            {/* CVSS Score */}
                            {vuln.cvssScore && (
                              <DetailCard>
                                <DetailLabel>
                                  <Award size={16} />
                                  CVSS Score
                                </DetailLabel>
                                <DetailValue>{vuln.cvssScore}</DetailValue>
                              </DetailCard>
                            )}

                            {/* Technical Analysis */}
                            {vuln.technicalDetails && (
                              <DetailCard>
                                <DetailLabel>
                                  <Terminal size={16} />
                                  Technical Analysis
                                </DetailLabel>
                                <DetailValue>{vuln.technicalDetails}</DetailValue>
                              </DetailCard>
                            )}

                            {/* Proof of Concept */}
                            {vuln.poc && (
                              <DetailCard>
                                <DetailLabel>
                                  <Code size={16} />
                                  Proof of Concept
                                </DetailLabel>
                                <DetailValue>
                                  {vuln.poc.startsWith("data:image") ? (
                                    <ImagePreview src={vuln.poc} alt="Proof of Concept" />
                                  ) : (
                                    <CodeBlock>{vuln.poc}</CodeBlock>
                                  )}
                                </DetailValue>
                              </DetailCard>
                            )}

                            {/* Remediation Steps */}
                            {vuln.remediation && (
                              <DetailCard>
                                <DetailLabel>
                                  <Lock size={16} />
                                  Remediation Steps
                                </DetailLabel>
                                <DetailValue>{vuln.remediation}</DetailValue>
                              </DetailCard>
                            )}

                            {/* Code Implementation */}
                            {vuln.codeRemediation && (
                              <DetailCard>
                                <DetailLabel>
                                  <Code size={16} />
                                  Code Implementation
                                </DetailLabel>
                                <DetailValue>
                                  <CodeBlock>{vuln.codeRemediation}</CodeBlock>
                                </DetailValue>
                              </DetailCard>
                            )}
                          </div>
                        </VulnDetails>
                      </VulnContent>
                    </VulnCard>
                  ));
                })}
              </VulnerabilityGrid>
            )}
          </Section>
        </Content>
      </Container>
    </>
  );
};

const AdvancedScanReportPage = () => {
  const { scan_type, scan_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scanData, setScanData] = useState([]);
  const [vulnerabilityData, setVulnerabilityData] = useState([]);

  useEffect(() => {
    if (!scan_type || !scan_id) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await apiRequest(`/api/report/website/${scan_id}`);

        if (!res.ok) {
          throw new Error(`Error: ${res.status} ${res.statusText}`);
        }

        const data = await res.json();
        setScanData(data.scanData || []);
        setVulnerabilityData(data.vulnerabilityScanData || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
        setScanData([]);
        setVulnerabilityData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [scan_type, scan_id]);

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner />
          <LoadingText>
            Loading Advanced Security Assessment Report...
          </LoadingText>
          <div style={{ fontSize: '0.95rem', color: '#64748b', textAlign: 'center', maxWidth: '400px' }}>
            Analyzing vulnerabilities and generating comprehensive report
          </div>
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Content>
          <ErrorContainer>
            <XCircle size={64} style={{ margin: '0 auto 2rem auto' }} />
            <div style={{ fontSize: '1.5rem', marginBottom: '1rem', fontWeight: '700' }}>
              Failed to Load Report
            </div>
            <div style={{ opacity: 0.9, fontSize: '1.1rem' }}>{error}</div>
            <ActionButtons>
              <ActionButton onClick={() => window.location.reload()}>
                <Activity size={16} />
                Retry Loading
              </ActionButton>
            </ActionButtons>
          </ErrorContainer>
        </Content>
      </Container>
    );
  }

  return (
    <AdvancedScanReport scanData={scanData} vulnerabilityData={vulnerabilityData} />
  );
};

export default AdvancedScanReportPage;